import React, { useEffect, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tooltip,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import {
  Person as PersonIcon,
  Cake as CakeIcon,
  Height as HeightIcon,
  Scale as ScaleIcon,
  People as PeopleIcon
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  border: 'none',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: '#f8fafc',
    '& fieldset': {
      borderColor: '#e2e8f0',
    },
    '&:hover fieldset': {
      borderColor: '#667eea',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
}));

const BasicInfo = ({ userData, handleInputChange }) => {
  const calculateAge = useCallback((birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const m = today.getMonth() - birthDateObj.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
      age--;
    }
    return age;
  }, []);

  const handleDateChange = (e) => {
    const birthDate = e.target.value;
    handleInputChange('birthDate', birthDate);
    const age = calculateAge(birthDate);
    handleInputChange('age', age);
  };

  useEffect(() => {
    if (userData.birthDate && userData.age === '') {
      const age = calculateAge(userData.birthDate);
      handleInputChange('age', age);
    }
  }, [userData.birthDate, userData.age, calculateAge, handleInputChange]);

  const handleSexChange = (sex) => {
    handleInputChange('sex', sex);
  };

  return (
    <Box>
      {/* Personal Info Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#e0e7ff' }}>
              <PersonIcon sx={{ color: '#667eea' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Información personal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cuéntanos sobre ti
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Tu nombre"
                value={userData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="¿Cómo te llamas?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Fecha de nacimiento"
                type="date"
                value={userData.birthDate ? userData.birthDate.split('T')[0] : ''}
                onChange={handleDateChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Género
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[
                  { value: 'Masculino', emoji: '👨' },
                  { value: 'Femenino', emoji: '👩' },
                  { value: 'Otro', emoji: '🧑' },
                ].map((option) => (
                  <Box
                    key={option.value}
                    onClick={() => handleSexChange(option.value)}
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: '12px',
                      border: '2px solid',
                      borderColor: userData.sex === option.value ? '#667eea' : '#e2e8f0',
                      bgcolor: userData.sex === option.value ? '#e0e7ff' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#667eea',
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 0.5 }}>{option.emoji}</Typography>
                    <Typography
                      variant="caption"
                      fontWeight={userData.sex === option.value ? 600 : 500}
                      color={userData.sex === option.value ? '#667eea' : '#64748b'}
                    >
                      {option.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Body Measurements Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#dbeafe' }}>
              <ScaleIcon sx={{ color: '#3b82f6' }} />
            </IconBox>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Medidas corporales
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Para calcular tus macros recomendados
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Altura"
                type="number"
                value={userData.heightCM || ''}
                onChange={(e) => handleInputChange('heightCM', e.target.value)}
                InputProps={{
                  endAdornment: <Typography color="text.secondary">cm</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                label="Peso actual"
                type="number"
                value={userData.weight || ''}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                InputProps={{
                  endAdornment: <Typography color="text.secondary">kg</Typography>,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Household Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconBox sx={{ bgcolor: '#dcfce7' }}>
              <PeopleIcon sx={{ color: '#22c55e' }} />
            </IconBox>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="600" color="#1e293b">
                Comensales en casa
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Para calcular cantidades en la lista de compra
              </Typography>
            </Box>
            <Tooltip title="Este valor se utilizará para calcular la cantidad de ingredientes en la lista de la compra">
              <IconButton size="small">
                <InfoIcon sx={{ color: '#94a3b8' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Box
              onClick={() => handleInputChange('comensalesCasa', Math.max(1, (userData.comensalesCasa || 2) - 1))}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#64748b',
                '&:hover': { bgcolor: '#e2e8f0' }
              }}
            >
              −
            </Box>
            <Typography variant="h3" fontWeight="700" color="#667eea" sx={{ minWidth: 60, textAlign: 'center' }}>
              {userData.comensalesCasa || 2}
            </Typography>
            <Box
              onClick={() => handleInputChange('comensalesCasa', Math.min(10, (userData.comensalesCasa || 2) + 1))}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#64748b',
                '&:hover': { bgcolor: '#e2e8f0' }
              }}
            >
              +
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default BasicInfo;
